import { useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { DialogPortal, DialogOverlay } from "@/components/ui/dialog";
import { getImageUrl } from "@/lib/api";

/**
 * Centered video lightbox for the Vitrin/Coverflow section. Deliberately built directly
 * on the shared Radix primitives (Portal/Overlay reused from components/ui/dialog.jsx)
 * rather than the generic <DialogContent>, because this needs a different size contract
 * (centered but NOT fullscreen, max-w-[960px]/max-h-[85vh]) and a close affordance that
 * is icon-only (no visible "Kapat/Close" text, aria-label covers the accessible name).
 *
 * The <video> element only exists in the tree while `open` is true - Radix's Dialog
 * Content is unmounted from the DOM when closed, which on its own guarantees this modal
 * never contributes a video network request on initial homepage load or while browsing
 * other Coverflow cards. The pause/empty-src cleanup below is extra defense so an
 * in-flight buffered response is released immediately on close, not just on unmount.
 *
 * Audio: per explicit product requirement, playback must NOT be muted - if the source
 * file has a real audio track it must be audible, with the native <video controls> bar
 * giving the user volume/mute control. No muted/defaultMuted attribute is ever set here.
 */
const ShowcaseVideoModal = ({ open, onOpenChange, item }) => {
  const videoRef = useRef(null);
  const [playbackError, setPlaybackError] = useState(false);

  // Reset any previous error state each time a new item is opened.
  useEffect(() => {
    if (open) setPlaybackError(false);
  }, [open, item?.id]);

  useEffect(() => {
    const v = videoRef.current;
    if (!open || !v) return;

    // Explicit play() call (in addition to the `autoPlay` attribute below) because some
    // browsers are more reliable about honoring a play() call made in direct response to
    // the user's click than the declarative autoplay attribute alone. If the browser
    // rejects unmuted autoplay (no/insufficient user-gesture activation) or the file
    // genuinely fails to decode, this surfaces as a neutral on-screen message rather
    // than a silently frozen player.
    const playResult = v.play();
    if (playResult && typeof playResult.catch === "function") {
      playResult.catch((err) => {
        console.warn("Vitrin video oynatma başlatılamadı:", err?.name || err);
        setPlaybackError(true);
      });
    }

    const handleError = () => {
      console.warn("Vitrin video oynatma hatası:", v.error?.code);
      setPlaybackError(true);
    };
    v.addEventListener("error", handleError);

    return () => {
      v.removeEventListener("error", handleError);
      // Release the media resource the moment the modal closes/unmounts - pausing alone
      // can leave a large in-flight buffered response alive in some browsers.
      v.pause();
      try {
        v.removeAttribute("src");
        v.load();
      } catch {
        // Some browsers throw if called at an inopportune readyState - safe to ignore,
        // the element itself is about to be unmounted by Radix regardless.
      }
    };
  }, [open, item?.id]);

  if (!item) return null;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="backdrop-blur-sm" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-1/2 z-50 w-[92vw] sm:w-auto max-w-[960px] max-h-[85vh] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[16px] bg-black shadow-2xl outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          data-testid="showcase-video-modal"
        >
          {/* Close button is the first focusable descendant in DOM order, so Radix's
              default onOpenAutoFocus behavior lands keyboard focus there - not on the
              <video> element - without needing any custom focus handling. */}
          <DialogPrimitive.Title className="sr-only">{item.title || "Vitrin videosu"}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {item.description || "Vitrin videosu oynatıcısı"}
          </DialogPrimitive.Description>

          <DialogPrimitive.Close
            aria-label="Kapat"
            data-testid="showcase-video-modal-close-button"
            className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
          >
            <X className="h-5 w-5" />
          </DialogPrimitive.Close>

          <div className="flex items-center justify-center bg-black" style={{ maxHeight: "85vh" }}>
            <video
              ref={videoRef}
              key={item.id}
              src={getImageUrl(item.media_path)}
              poster={item.poster_path ? getImageUrl(item.poster_path) : undefined}
              controls
              autoPlay
              playsInline
              className="max-h-[85vh] w-full object-contain"
              data-testid="showcase-video-modal-player"
            >
              Tarayıcınız video oynatmayı desteklemiyor.
            </video>
          </div>

          {playbackError && (
            <div
              className="absolute inset-0 flex items-center justify-center bg-black/80 px-6 text-center"
              data-testid="showcase-video-modal-error"
            >
              <p className="text-sm text-white">Video şu anda oynatılamıyor.</p>
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogPrimitive.Root>
  );
};

export default ShowcaseVideoModal;
